import React, { useState, useRef, useEffect } from 'react';
import CalendarVoting from './CalendarVoting';
import { SurveyData, Question } from '../types/survey';
import './Survey.css';

const Survey: React.FC = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const [unlockedSections, setUnlockedSections] = useState(new Set([0])); // Start with first section unlocked
  const [pendingScrollTarget, setPendingScrollTarget] = useState<number | null>(null);
  const [surveyData, setSurveyData] = useState<SurveyData>({
    email: '',
    calendarPreference: null,
    calendarRanking: {},
    answers: {}
  });
  const [email, setEmail] = useState('');
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isScrollingProgrammatically = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isScrollingProgrammatically.current) {
            const sectionIndex = sectionRefs.current.findIndex(ref => ref === entry.target);
            if (sectionIndex !== -1) {
              setCurrentSection(sectionIndex);
            }
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: '-10% 0px -10% 0px'
      }
    );

    sectionRefs.current.forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => {
      sectionRefs.current.forEach(ref => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);

  // Prevent ALL scrolling - mouse, touch, keyboard; sections manage their own overflow when needed
  useEffect(() => {
    const getEventPath = (event: Event): EventTarget[] => (
      typeof event.composedPath === 'function' ? event.composedPath() : []
    );

    const findScrollableContainer = (event: Event): HTMLElement | null => {
      const path = getEventPath(event);
      for (const entry of path) {
        if (entry instanceof HTMLElement && entry.classList.contains('section-content')) {
          if (entry.scrollHeight > entry.clientHeight + 1) {
            return entry;
          }
        }
      }

      if (event.target instanceof HTMLElement) {
        const fallback = event.target.closest('.section-content');
        if (fallback instanceof HTMLElement && fallback.scrollHeight > fallback.clientHeight + 1) {
          return fallback;
        }
      }

      return null;
    };

    const isInteractivePath = (event: Event): boolean => {
      const path = getEventPath(event);
      for (const entry of path) {
        if (entry instanceof HTMLElement) {
          if (entry.isContentEditable || entry.matches(`button, input, select, textarea, label, a, [role='button']`)) {
            return true;
          }
        }
      }

      if (event.target instanceof HTMLElement) {
        if (event.target.closest(`button, input, select, textarea, label, a, [role='button']`)) {
          return true;
        }
        if (event.target.isContentEditable) {
          return true;
        }
      }

      return false;
    };

    // Prevent wheel scrolling
    const preventWheel = (e: WheelEvent) => {
      if (!isScrollingProgrammatically.current) {
        if (findScrollableContainer(e) || isInteractivePath(e)) {
          return;
        }
        e.preventDefault();
      }
    };

    // Prevent touch scrolling unless the gesture is inside a scrollable section or interactive element
    const preventTouch = (e: TouchEvent) => {
      if (!isScrollingProgrammatically.current) {
        if (findScrollableContainer(e) || isInteractivePath(e)) {
          return;
        }
        e.preventDefault();
      }
    };

    // Prevent keyboard scrolling
    const preventKeyboard = (e: KeyboardEvent) => {
      if (!isScrollingProgrammatically.current && !isInteractivePath(e)) {
        const scrollKeys = [
          'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
          'Space', 'PageUp', 'PageDown', 'Home', 'End'
        ];
        if (scrollKeys.includes(e.code)) {
          e.preventDefault();
        }
      }
    };

    // Prevent scroll events bubbling to document unless from a scrollable section or interactive element
    const preventScroll = (e: Event) => {
      if (!isScrollingProgrammatically.current) {
        if (findScrollableContainer(e) || isInteractivePath(e)) {
          return;
        }
        e.preventDefault();
        return false;
      }
    };

    // Add all event listeners with { passive: false } to allow preventDefault
    document.addEventListener('wheel', preventWheel, { passive: false });
    document.addEventListener('touchstart', preventTouch, { passive: false });
    document.addEventListener('touchmove', preventTouch, { passive: false });
    document.addEventListener('keydown', preventKeyboard, { passive: false });
    document.addEventListener('scroll', preventScroll, { passive: false });

    return () => {
      document.removeEventListener('wheel', preventWheel);
      document.removeEventListener('touchstart', preventTouch);
      document.removeEventListener('touchmove', preventTouch);
      document.removeEventListener('keydown', preventKeyboard);
      document.removeEventListener('scroll', preventScroll);
    };
  }, []);

  // Handle pending scroll after sections are rendered
  useEffect(() => {
    if (pendingScrollTarget !== null) {
      const targetRef = sectionRefs.current[pendingScrollTarget];
      if (targetRef) {
        setTimeout(() => {
          scrollToSection(pendingScrollTarget);
          setPendingScrollTarget(null);
        }, 50); // Small delay to ensure rendering is complete
      }
    }
  }, [unlockedSections, pendingScrollTarget]);

  const questions: Question[] = [
    {
      id: 'q1',
      text: 'When would you prefer the school year to begin?',
      type: 'radio',
      required: true,
      options: [
        { value: 'early-august', label: 'Early August (around August 3)' },
        { value: 'end-august', label: 'End of August (around August 27)' },
        { value: 'no-preference', label: 'No preference' }
      ]
    },
    {
      id: 'q2',
      text: 'What is most important to you regarding holiday breaks?',
      type: 'radio',
      required: true,
      options: [
        { value: 'current-breaks', label: 'Current breaks during Fall, Thanksgiving, Winter, and Spring' },
        { value: 'shorter-breaks', label: 'Shorter breaks during Fall, and Thanksgiving' },
        { value: 'no-preference', label: 'No preference' }
      ]
    },
    {
      id: 'q3',
      text: 'Do you prefer Spring Break to align with Easter?',
      type: 'radio',
      required: true,
      options: [
        { value: 'align-easter', label: 'Yes – Align with Easter' },
        { value: 'separate', label: 'No – Keep it separate' },
        { value: 'no-preference', label: 'No preference' }
      ]
    },
    {
      id: 'q4',
      text: 'How important is it to your family that our school calendar aligns closely with nearby districts (for siblings, childcare, or community activities)?',
      type: 'radio',
      required: true,
      options: [
        { value: 'very-important', label: 'Very important – We prefer strong alignment with nearby districts' },
        { value: 'somewhat-important', label: 'Somewhat important – Alignment helps but is not a top priority' },
        { value: 'not-important', label: 'Not important – We prioritize our own district\'s calendar' },
        { value: 'no-preference', label: 'No preference' }
      ]
    },
    {
      id: 'q5',
      text: 'When choosing a calendar, which factor matters most to your family?',
      type: 'radio',
      required: true,
      options: [
        { value: 'shorter-summer', label: 'Shorter summer, earlier start and longer holiday breaks' },
        { value: 'longer-summer', label: 'Longer summer, later start, shorter breaks and later end date' },
        { value: 'balance', label: 'Balance of both / no preference' }
      ]
    },
    {
      id: 'q6',
      text: 'Is there anything else you would like us to consider when deciding on the school calendar?',
      type: 'textarea',
      required: false
    }
  ];

  // Total sections: 0=CTA, 1=Calendar, 2=Questions Intro, 3-9=Questions, 10=Complete
  const totalSections = 3 + questions.length + 1;

  const scrollToSection = (sectionIndex: number) => {
    const targetRef = sectionRefs.current[sectionIndex];
    if (targetRef) {
      isScrollingProgrammatically.current = true;
      targetRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setCurrentSection(sectionIndex);
      setTimeout(() => {
        isScrollingProgrammatically.current = false;
      }, 500);
    }
  };

  const unlockAndScrollTo = (sectionIndex: number) => {
    setUnlockedSections(prev => new Set([...Array.from(prev), sectionIndex]));
    setPendingScrollTarget(sectionIndex);
  };

  const handleVotingComplete = (preference: number, ranking: Record<number, number>) => {
    setSurveyData({
      ...surveyData,
      email,
      calendarPreference: preference,
      calendarRanking: ranking
    });
    unlockAndScrollTo(2); // Unlock and scroll to questions intro
  };

  const handleAnswerChange = (questionId: string, value: any) => {
    setSurveyData({
      ...surveyData,
      answers: {
        ...surveyData.answers,
        [questionId]: value
      }
    });
  };

  const handleRankingChange = (questionId: string, optionValue: string, rank: number) => {
    const currentRanking = surveyData.answers[questionId] || {};
    setSurveyData({
      ...surveyData,
      answers: {
        ...surveyData.answers,
        [questionId]: {
          ...currentRanking,
          [optionValue]: rank
        }
      }
    });
  };

  const isQuestionAnswered = (question: Question) => {
    const answer = surveyData.answers[question.id];
    if (question.required) {
      return answer && answer !== '';
    }
    return true; // Optional questions are always "answered"
  };

  const canProceedToNext = (questionIndex: number) => {
    const question = questions[questionIndex];
    return isQuestionAnswered(question);
  };

  const handleQuestionnaireComplete = async () => {
    try {
      // Submit survey data to backend
      const response = await fetch('/api/submit-survey', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(surveyData),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Survey submitted successfully:', result);
        unlockAndScrollTo(totalSections - 1); // Unlock and scroll to complete section
      } else {
        console.error('Failed to submit survey:', response.statusText);
        // Still proceed to completion even if submission fails
        unlockAndScrollTo(totalSections - 1);
      }
    } catch (error) {
      console.error('Error submitting survey:', error);
      // Still proceed to completion even if submission fails
      unlockAndScrollTo(totalSections - 1);
    }
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const renderQuestion = (question: Question, questionIndex: number) => {
    const sectionIndex = 3 + questionIndex;

    switch (question.type) {
      case 'radio':
        return (
          <div
            key={question.id}
            ref={el => { sectionRefs.current[sectionIndex] = el; }}
            className={`survey-section question-section ${currentSection === sectionIndex ? 'active' : ''}`}
          >
            <div className="section-content">
              <div className="question-header">
                <span className="question-number">Question {questionIndex + 1} of {questions.length}</span>
                <h2>{question.text}</h2>
              </div>
              <div className="radio-options">
                {question.options?.map((option) => (
                  <label key={option.value} className="radio-option">
                    <input
                      type="radio"
                      name={question.id}
                      value={option.value}
                      checked={surveyData.answers[question.id] === option.value}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    />
                    <span className="radio-checkmark"></span>
                    <span className="option-text">{option.label}</span>
                  </label>
                ))}
              </div>
              <div className="section-navigation">
                {currentSection > 0 && (
                  <button
                    className="nav-btn back-btn"
                    onClick={() => scrollToSection(currentSection - 1)}
                  >
                    ← Back
                  </button>
                )}
                {canProceedToNext(questionIndex) && (
                  <button
                    className="nav-btn next-btn"
                    onClick={() => {
                      if (questionIndex === questions.length - 1) {
                        handleQuestionnaireComplete();
                      } else {
                        unlockAndScrollTo(currentSection + 1);
                      }
                    }}
                  >
                    {questionIndex === questions.length - 1 ? 'Complete Survey' : 'Next →'}
                  </button>
                )}
              </div>
            </div>
          </div>
        );

      case 'textarea':
        return (
          <div
            key={question.id}
            ref={el => { sectionRefs.current[sectionIndex] = el; }}
            className={`survey-section question-section ${currentSection === sectionIndex ? 'active' : ''}`}
          >
            <div className="section-content">
              <div className="question-header">
                <span className="question-number">Question {questionIndex + 1} of {questions.length}</span>
                <h2>{question.text}</h2>
                {!question.required && <span className="optional-label">(Optional)</span>}
              </div>
              <textarea
                value={surveyData.answers[question.id] || ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                placeholder="Please share your thoughts..."
                rows={6}
                className="textarea-input"
              />
              <div className="section-navigation">
                {currentSection > 0 && (
                  <button
                    className="nav-btn back-btn"
                    onClick={() => scrollToSection(currentSection - 1)}
                  >
                    ← Back
                  </button>
                )}
                <button
                  className="nav-btn next-btn"
                  onClick={() => {
                    if (questionIndex === questions.length - 1) {
                      handleQuestionnaireComplete();
                    } else {
                      scrollToSection(currentSection + 1);
                    }
                  }}
                >
                  {questionIndex === questions.length - 1 ? 'Complete Survey' : 'Next →'}
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="survey-container">
      {/* Section 0: Initial CTA - Always visible */}
      {unlockedSections.has(0) && (
        <div
          ref={el => { sectionRefs.current[0] = el; }}
          className={`survey-section intro-section ${currentSection === 0 ? 'active' : ''}`}
        >
          <div className="section-content">
            <h1>Calendar Survey: <br />2026–2027 School Year</h1>
            <p className="intro-description">
              Help us choose the best calendar for our district.
            </p>
            <div className="email-input-section">
              <label htmlFor="email-input" className="email-label">
                Email Address (Required)
              </label>
              <input
                id="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && isValidEmail(email)) {
                    unlockAndScrollTo(1);
                  }
                }}
                placeholder="Enter your email address"
                className="email-input"
                required
              />
            </div>
            <button
              className={`cta-btn ${!isValidEmail(email) ? 'disabled' : ''}`}
              onClick={() => unlockAndScrollTo(1)}
              disabled={!isValidEmail(email)}
            >
              Start Survey
            </button>
          </div>
        </div>
      )}

      {/* Section 1: Calendar Voting */}
      {unlockedSections.has(1) && (
        <div
          ref={el => { sectionRefs.current[1] = el; }}
          className={`survey-section calendar-section ${currentSection === 1 ? 'active' : ''}`}
        >
          <div className="section-content">
            <CalendarVoting onComplete={handleVotingComplete} />
          </div>
        </div>
      )}

      {/* Section 2: Questions Introduction */}
      {unlockedSections.has(2) && (
        <div
          ref={el => { sectionRefs.current[2] = el; }}
          className={`survey-section questions-intro-section ${currentSection === 2 ? 'active' : ''}`}
        >
          <div className="section-content">
            <h1>Part 2: Your Preferences</h1>
            <p className="intro-description">
              Now we'd like to learn more about your calendar preferences.
              You'll answer {questions.length} questions, one at a time.
            </p>
            <button
              className="cta-btn"
              onClick={() => unlockAndScrollTo(3)}
            >
              Begin Questions
            </button>
          </div>
        </div>
      )}

      {/* Sections 3-9: Individual Questions */}
      {questions.map((question, index) => {
        const sectionIndex = 3 + index;
        return unlockedSections.has(sectionIndex) ? renderQuestion(question, index) : null;
      })}

      {/* Section 10: Complete */}
      {unlockedSections.has(totalSections - 1) && (
        <div
          ref={el => { sectionRefs.current[totalSections - 1] = el; }}
          className={`survey-section complete-section ${currentSection === totalSections - 1 ? 'active' : ''}`}
        >
          <div className="section-content">
            <div className="complete-icon">✓</div>
            <h1>Thank You!</h1>
            <p>Your survey responses have been recorded. We appreciate your participation in helping us choose the best calendar for our school district.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Survey;